import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';
import { campaignsService, Campaign } from '../services/campaigns';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface ICP {
  id: string;
  name: string;
}

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAIGenerate, setOpenAIGenerate] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [icps, setIcps] = useState<ICP[]>([]);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    goal: 'lead_generation',
    budget: 0,
    icp_id: '',
    campaign_type: 'email',
    channels: [] as string[],
    start_date: '',
    end_date: '',
  });
  const [aiGenerateForm, setAiGenerateForm] = useState({
    goal: '',
    target_audience: '',
    budget: 1000,
    duration_days: 30,
    channels: [] as string[],
  });

  const steps = ['Campaign Details', 'Target Audience', 'Channels & Schedule'];
  const workspaceId = 'default-workspace';

  useEffect(() => {
    loadCampaigns();
    loadICPs();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/campaigns`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setCampaigns(response.data || []);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load campaigns:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadICPs = async () => {
    try {
      const response = await axios.get(`${API_URL}/icps`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setIcps(response.data || []);
    } catch (err: any) {
      console.error('Failed to load ICPs:', err);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      await axios.post(
        `${API_URL}/campaigns`,
        newCampaign,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
      setOpenDialog(false);
      setActiveStep(0);
      setNewCampaign({
        name: '',
        goal: 'lead_generation',
        budget: 0,
        icp_id: '',
        campaign_type: 'email',
        channels: [],
        start_date: '',
        end_date: '',
      });
      loadCampaigns();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create campaign');
    }
  };

  const handleAIGenerateCampaign = async () => {
    try {
      setLoading(true);
      
      // Step 1: Create campaign workflow via orchestrator
      const response = await axios.post(
        `${API_URL}/campaigns/generate`,
        aiGenerateForm,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
      
      const workflowId = response.data.workflow_id;
      setSuccess(`AI Campaign workflow created! Workflow ID: ${workflowId}. Executing multi-agent workflow...`);
      
      // Step 2: Automatically execute the workflow (orchestrator delegates to agents)
      await axios.post(
        `${API_URL}/workflows/${workflowId}/execute`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
      
      setOpenAIGenerate(false);
      setSuccess(`AI Campaign executing! Agents working: Campaign Planner → Content Architect → Content Generation → Compliance → Posting. View progress in Workflows page.`);
      setAiGenerateForm({
        goal: '',
        target_audience: '',
        budget: 1000,
        duration_days: 30,
        channels: [],
      });
      
      // Reload campaigns after workflow completes
      setTimeout(() => loadCampaigns(), 5000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate campaign with AI');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleChannelToggle = (channel: string) => {
    setNewCampaign(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel]
    }));
  };

  const getStatusColor = (status: string): any => {
    const colors: any = {
      active: 'success',
      paused: 'warning',
      completed: 'default',
      planned: 'info',
    };
    return colors[status] || 'default';
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 0 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
        width: '100%'
      }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Campaigns</Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant="outlined"
            startIcon={<AIIcon />}
            onClick={() => setOpenAIGenerate(true)}
            color="secondary"
            fullWidth={window.innerWidth < 600}
          >
            Generate with AI
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Create Campaign
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : campaigns.length > 0 ? (
        <Grid container spacing={3}>
          {campaigns.map((campaign) => (
            <Grid item xs={12} md={6} lg={4} key={campaign.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {campaign.name}
                  </Typography>
                  <Box display="flex" gap={1} mb={2}>
                    <Chip label={campaign.status} color={getStatusColor(campaign.status)} size="small" />
                    <Chip label={campaign.goal} size="small" variant="outlined" />
                  </Box>
                  {campaign.budget && (
                    <Typography variant="body2" color="textSecondary">
                      Budget: ${campaign.budget.toLocaleString()}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography color="textSecondary">
            No campaigns yet. Create your first campaign to get started.
          </Typography>
        </Paper>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Campaign</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box>
              <TextField
                fullWidth
                label="Campaign Name"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                margin="normal"
                required
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Campaign Type</InputLabel>
                <Select
                  value={newCampaign.campaign_type}
                  label="Campaign Type"
                  onChange={(e) => setNewCampaign({ ...newCampaign, campaign_type: e.target.value })}
                >
                  <MenuItem value="email">Email Campaign</MenuItem>
                  <MenuItem value="social">Social Media Campaign</MenuItem>
                  <MenuItem value="content">Content Marketing</MenuItem>
                  <MenuItem value="outbound">Outbound Sales</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Goal</InputLabel>
                <Select
                  value={newCampaign.goal}
                  label="Goal"
                  onChange={(e) => setNewCampaign({ ...newCampaign, goal: e.target.value })}
                >
                  <MenuItem value="lead_generation">Lead Generation</MenuItem>
                  <MenuItem value="brand_awareness">Brand Awareness</MenuItem>
                  <MenuItem value="engagement">Engagement</MenuItem>
                  <MenuItem value="conversion">Conversion</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                type="number"
                label="Budget"
                value={newCampaign.budget}
                onChange={(e) => setNewCampaign({ ...newCampaign, budget: Number(e.target.value) })}
                margin="normal"
                InputProps={{ startAdornment: '$' }}
              />
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <FormControl fullWidth margin="normal">
                <InputLabel>Select ICP</InputLabel>
                <Select
                  value={newCampaign.icp_id}
                  label="Select ICP"
                  onChange={(e) => setNewCampaign({ ...newCampaign, icp_id: e.target.value })}
                >
                  {icps.map((icp) => (
                    <MenuItem key={icp.id} value={icp.id}>
                      {icp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Select your target Ideal Customer Profile for this campaign. This will help tailor the content and targeting.
              </Typography>
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Select Channels
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newCampaign.channels.includes('email')}
                      onChange={() => handleChannelToggle('email')}
                    />
                  }
                  label="Email"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newCampaign.channels.includes('linkedin')}
                      onChange={() => handleChannelToggle('linkedin')}
                    />
                  }
                  label="LinkedIn"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newCampaign.channels.includes('twitter')}
                      onChange={() => handleChannelToggle('twitter')}
                    />
                  }
                  label="Twitter"
                />
              </FormGroup>

              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={newCampaign.start_date}
                onChange={(e) => setNewCampaign({ ...newCampaign, start_date: e.target.value })}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={newCampaign.end_date}
                onChange={(e) => setNewCampaign({ ...newCampaign, end_date: e.target.value })}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setActiveStep(0); }}>Cancel</Button>
          {activeStep > 0 && (
            <Button onClick={handleBack}>Back</Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={handleNext} disabled={
              (activeStep === 0 && !newCampaign.name) ||
              (activeStep === 1 && !newCampaign.icp_id)
            }>
              Next
            </Button>
          ) : (
            <Button variant="contained" onClick={handleCreateCampaign} disabled={newCampaign.channels.length === 0}>
              Create Campaign
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* AI Generate Campaign Dialog */}
      <Dialog open={openAIGenerate} onClose={() => setOpenAIGenerate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AIIcon color="secondary" />
            Generate Campaign with AI
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Our multi-agent AI system will create a complete campaign strategy including content calendar, 
            creative assets, and posting schedule.
          </Alert>
          <TextField
            fullWidth
            label="Campaign Goal"
            placeholder="e.g., Generate 100 qualified leads in Q1"
            value={aiGenerateForm.goal}
            onChange={(e) => setAiGenerateForm({ ...aiGenerateForm, goal: e.target.value })}
            margin="normal"
            required
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Target Audience"
            placeholder="e.g., B2B SaaS companies with 50-200 employees"
            value={aiGenerateForm.target_audience}
            onChange={(e) => setAiGenerateForm({ ...aiGenerateForm, target_audience: e.target.value })}
            margin="normal"
            required
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            type="number"
            label="Budget (USD)"
            value={aiGenerateForm.budget}
            onChange={(e) => setAiGenerateForm({ ...aiGenerateForm, budget: Number(e.target.value) })}
            margin="normal"
            InputProps={{ startAdornment: '$' }}
          />
          <TextField
            fullWidth
            type="number"
            label="Duration (Days)"
            value={aiGenerateForm.duration_days}
            onChange={(e) => setAiGenerateForm({ ...aiGenerateForm, duration_days: Number(e.target.value) })}
            margin="normal"
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Select Channels
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={aiGenerateForm.channels.includes('email')}
                  onChange={() => {
                    setAiGenerateForm(prev => ({
                      ...prev,
                      channels: prev.channels.includes('email')
                        ? prev.channels.filter(c => c !== 'email')
                        : [...prev.channels, 'email']
                    }));
                  }}
                />
              }
              label="Email"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={aiGenerateForm.channels.includes('linkedin')}
                  onChange={() => {
                    setAiGenerateForm(prev => ({
                      ...prev,
                      channels: prev.channels.includes('linkedin')
                        ? prev.channels.filter(c => c !== 'linkedin')
                        : [...prev.channels, 'linkedin']
                    }));
                  }}
                />
              }
              label="LinkedIn"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={aiGenerateForm.channels.includes('twitter')}
                  onChange={() => {
                    setAiGenerateForm(prev => ({
                      ...prev,
                      channels: prev.channels.includes('twitter')
                        ? prev.channels.filter(c => c !== 'twitter')
                        : [...prev.channels, 'twitter']
                    }));
                  }}
                />
              }
              label="Twitter"
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAIGenerate(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAIGenerateCampaign}
            disabled={!aiGenerateForm.goal || !aiGenerateForm.target_audience || aiGenerateForm.channels.length === 0 || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <AIIcon />}
          >
            {loading ? 'Creating Workflow...' : 'Generate Campaign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
